/**
 * Fresh locations/scanners for the Upload dialog, fetched every time it opens.
 *
 * `DocumentsScreen`'s own `useDocuments` hook loads these too, but only
 * refetches on a filter change, a manual reload, or the live-poll timer — never
 * because the Upload dialog opened. A location or scanner added in Settings
 * while the Documents tab is already open would not show up here until one of
 * those unrelated triggers happened to fire. This hook is independent of that
 * one so the dropdowns are always current as of the moment someone opens the
 * dialog, not as of whenever the list last happened to load.
 *
 * Best-effort, same fallback as `useDocuments`/`useDocument`: every tenant
 * reader holds `registries:read`, but a failed fetch still resolves to an
 * empty list rather than blocking the dialog.
 */
import { useEffect, useState } from 'react';

import type { Location, Scanner } from '../../api/types';
import { useSession } from '../../auth/session';

export interface UploadRegistries {
  locations: Location[];
  scanners: Scanner[];
}

const EMPTY: UploadRegistries = { locations: [], scanners: [] };

export function useUploadRegistries(open: boolean): UploadRegistries {
  const { api } = useSession();
  const [data, setData] = useState<UploadRegistries>(EMPTY);

  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    const signal = controller.signal;
    void (async () => {
      const [locationsPage, scannersPage] = await Promise.all([
        api.settings.locations.list(signal).catch(() => null),
        api.settings.scanners.list(signal).catch(() => null),
      ]);
      if (signal.aborted) return;
      setData({
        locations: locationsPage?.items ?? [],
        scanners: scannersPage?.items ?? [],
      });
    })();
    return () => {
      controller.abort();
    };
  }, [api, open]);

  return data;
}
